import _ from 'lodash';
import {
  call,
  put,
  take,
  select,
  race,
  fork,
  takeLatest,
} from 'redux-saga/effects';
import {
  fetchWithTimeout,
  myAxaFetchWithToken,
} from '../../../utils/myAxaFetchWithToken';
import { getToken } from '../../../selectors/authSelectors';
import env from '../../../env/';
import {
  FETCH_PROFILE_FEATURES_SUCCESS,
  FETCH_PROFILE_FEATURES_FAILURE,
  FETCH_HEALTH_NEWS_SUCCESS,
  FETCH_HEALTH_NEWS_FAILURE,
  FETCH_HAVE_READ_NEW_FEATURE_SUCCESS,
  FETCH_HAVE_READ_NEW_FEATURE_FAILURE,
  UPDATE_HAVE_READ_NEW_FEATURE_STATE,
  FETCH_MARKETPLACE_PROMOTION_NEWS_SUCCESS,
  FETCH_MARKETPLACE_PROMOTION_NEWS_FAILURE,
  PUSH_HAVE_READ_NEW_FEATURE,
  FETCH_AXA_BLOG_FAILURE,
  FETCH_AXA_BLOG_SUCCESS,
  FETCH_CONTENTO_AVATORS_SUCCESS,
  FETCH_CONTENTO_AVATORS_FAILURE,
  SET_AVATORS,
} from '../actions';
import { FRAME_APP_MOUNTED } from '../../../actions/frameActions';
import { LOCATION_CHANGE } from 'connected-react-router';
import { getLanguage } from '../../../selectors/userSelectors';
import { putCustState, fetchCustState } from '../../../actions/userActions';
import {
  OPEN_TNC,
  CLOSE_TNC,
  ACCEPT_TNC,
  closeTNC,
  FETCH_TC_FAILURE,
} from '../../../actions/uiActions';
import { signOut } from '../../../actions/authActions';
import { getTNCDialogAttributes } from '../../../selectors/uiSelectors';
import browserHistory from '../../../stores/browserHistory';
import { getValidToken, isApp } from '../../../utils';
import {
  getHomepageNewsApiUrl,
  getMarketPlaceNewsApiUrl,
  getAvatorsConfig,
} from '../../../selectors/configSelectors';
import uuid from 'uuid';
import { handleGaError } from '../../../utils/generalGaEventAnalytics';
import moment from 'moment';
import { AxaBlogType } from '../types/AxaBlogType';

const match = env.WEBAPP_VERSION.match(/^[0-9]+\.[0-9]+\.[0-9]+/);
const webAppVersion = match ? match[0] : ``;
const lang = getLanguage();

function* fetchProfileFeatures(): Iterable<any> {
  do {
    try {
      // only for PO
      const isPo = yield select(
        (state) => state['../../../app']['moduleConfig']['emma-insurance'],
      );
      if (isPo) {
        const url = yield select(
          (state) =>
            state['../../../app']['moduleConfig']['emma-insurance'][
              'featuresApiUrl'
            ],
        );
        const token = select(getToken);
        if (token) {
          const res = yield call(myAxaFetchWithToken, url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-AXA-APP-CONFIG-VERSION': '0.0.0',
              'X-AXA-APP-INFO': `${env.APP_VERSION_USER_AGENT_PREFIX}/WEB/${webAppVersion}-${env.APP_VERSION_ENV}`, // e.g. MYAXA_HK/IOS/3.1.0-uat
              'X-AXA-POLICY-FETCH-TIME': '0000-01-01T00:00:00Z',
            },
          });
          const data = yield res.json();
          const featuresItems = _.get(data, `features`, {});
          yield put({ type: FETCH_PROFILE_FEATURES_SUCCESS, featuresItems });
        }
      }
    } catch (e) {
      yield put({ type: FETCH_PROFILE_FEATURES_FAILURE, error: e });
    }
    while (true) {
      const action = yield take(LOCATION_CHANGE);
      if (action.payload.location.pathname === '/home') {
        break;
      }
    }
  } while (true);
}

function* fetchHealthNews(): Iterable<any> {
  do {
    try {
      const newsApiUrl = yield select(getHomepageNewsApiUrl);
      const url = `${newsApiUrl.contentoNewsUrl}`;
      const res = yield call(fetchWithTimeout, url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          category: 'home',
          page: '1',
          lang: getLanguage(),
          pageSize: '3',
          after: '',
        }),
      });
      const data = yield res.json();
      const news = data.articles.results;
      yield put({ type: FETCH_HEALTH_NEWS_SUCCESS, news });
    } catch (e) {
      yield put({ type: FETCH_HEALTH_NEWS_FAILURE, error: e });
    }
    while (true) {
      const action = yield take(LOCATION_CHANGE);
      if (action.payload.location.pathname === '/home') {
        break;
      }
    }
  } while (true);
}

function* fetchAxaBlog(): Iterable<any> {
  do {
    try {
      const newsApiUrl = yield select(getHomepageNewsApiUrl);
      const url = `${newsApiUrl.axaBlogApiUrl.replace(`\${language}`, lang)}`;
      const res = yield call(myAxaFetchWithToken, url, {
        method: 'GET',
        headers: {
          'Accept-Language': getLanguage(),
          'x-axahk-msgid': uuid(),
          'x-prismic-fields': 'short_description,read_time,article_title,banner_title,publish_date,image,category_label,show_in_app,show_in_portal',
        },
      });
      const data = yield res.json();

      // 2024rls4 move cip-emma-document logic to frontend
      const limit = +new URLSearchParams(url)?.get('limit') || 5;
      const payload = (data as AxaBlogType[])
        .filter((item) => item.showInPortal && item.publishDate !== '')
        .sort((a, b) =>
          moment(a.publishDate).isBefore(moment(b.publishDate)) ? 1 : -1,
        )
        .slice(0, limit);

      yield put({
        type: FETCH_AXA_BLOG_SUCCESS,
        data: payload,
      });
    } catch (e) {
      yield put({ type: FETCH_AXA_BLOG_FAILURE, error: e });
    }
    while (true) {
      const action = yield take(LOCATION_CHANGE);
      if (action.payload.location.pathname === '/home') {
        break;
      }
    }
  } while (true);
}

function* fetchMarketplacePromotionNews(): Iterable<any> {
  do {
    try {
      const newsApiUrl = yield select(getMarketPlaceNewsApiUrl);
      const url = `${newsApiUrl.promotionalOffersApiUrl.replace(
        `\${language}`,
        lang,
      )}`;
      const token = select(getToken);
      if (token) {
        const res = yield call(myAxaFetchWithToken, url, {
          method: 'GET',
          headers: {
            'Accept-Language': lang,
            'x-axahk-msgid': uuid(),
          },
        });
        const data = yield res.json();
        const news = data.retrieveDocumentListResponse;
        yield put({ type: FETCH_MARKETPLACE_PROMOTION_NEWS_SUCCESS, news });
      }
    } catch (e) {
      yield put({ type: FETCH_MARKETPLACE_PROMOTION_NEWS_FAILURE, error: e });
    }
    while (true) {
      const action = yield take(LOCATION_CHANGE);
      if (action.payload.location.pathname === '/home') {
        break;
      }
    }
  } while (true);
}

const TnCKey = 'EmmaRegistrationTnC-202401';
const TnCChannel = 'emma';

function* fetchTermsAndCondition() {
  try {
    const res = yield fetchCustState(TnCKey, TnCChannel);
    const showDialog = shouldShowTncDialog(res);

    if (showDialog) {
      yield browserHistory.push(`/home`);
      yield put({
        type: OPEN_TNC,
        payload: {},
      });

      const { OK, CLOSE } = yield race({
        OK: take(ACCEPT_TNC),
        CLOSE: take(CLOSE_TNC),
      });
      if (OK) {
        const tncDialog = yield select(getTNCDialogAttributes);
        let tncValue, dmValue, picValue, piplValue;
        tncValue = tncDialog.tnc_first_checkbox ? 'Y' : 'N';
        dmValue = tncDialog.tnc_second_checkbox ? 'Y' : 'N';
        picValue = tncDialog.tnc_third_checkbox ? 'Y' : 'N';
        piplValue = tncDialog.tnc_4_checkbox && tncDialog.tnc_5_checkbox && tncDialog.tnc_6_checkbox ? 'Y' : 'N';

        const custValues = {
          TNC: tncValue,
          DM: dmValue,
          PIC: picValue,
          // PIPL: piplValue
          PIPL: 'N'
        };
        const dmValues = {
          DM2: dmValue,
        };
        yield putCustState(TnCKey, JSON.stringify(custValues), TnCChannel);
        yield putCustState('EmmaDM', JSON.stringify(dmValues), 'emma');
        yield put(closeTNC(null));
        return;
      }
      if (CLOSE) {
        yield put(signOut());
      }
    } else {
      return;
    }
  } catch (e) {
    yield put({ type: FETCH_TC_FAILURE, error: e });
  }
}

function shouldShowTncDialog(res: any) {
  let showDialog = false;
  if (res === '') {
    return true;
  } else {
    let response = JSON.parse(res);
    try {
      // try catch parsing as a fix for some account having issue with over stringified cust value
      // caused by the postCustState() issue in putTncState() during the development of HKEMMA-62587,
      // which putTncState() is now fixed. (in mobile)
      response = JSON.parse(response);
    } catch (e) {
      // ignore, not affected normal account that need not be parsed (in mobile)
    }
    // showDialog = !(response.TNC === 'Y' && response.PIC === 'Y' && response.PIPL === 'Y');
    showDialog = !(response.TNC === 'Y' && response.PIC === 'Y');
  }
  return showDialog;
}

function* pushEventToGA() {
  const tcWindow: any = window;
  const token = getToken(yield select());
  const aToken = getValidToken(token);

  if (aToken) {
    try {
      tcWindow.dataLayer.push({
        event: 'screenView',
        page: `/emma-webportal/homepage`,
        userId: aToken,
      });

      // HKEMMA-100917 - GA3 Decommission - Remove GA3
      // if (!isApp()) {
      //   tcWindow.tC.event.virtualPageview(tcWindow, {
      //     page: `/emma-webportal/homepage`,
      //     AXAID: aToken,
      //   });
      // }
    } catch (e) {
      handleGaError(e);
    }
  }
}

export function* fetchContentoAvators(): Iterable<any> {
  do {
    const avatorsConfig = yield select(getAvatorsConfig);
    if (!avatorsConfig.avators && avatorsConfig.contentoUrl) {
      try {
        const res = yield call(myAxaFetchWithToken, avatorsConfig.contentoUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });
        if (res && res.status === 200) {
          const data = yield res.json();
          const {
            chatbotIconHome,
            chatbotIconOther,
            chatbotIconBubble,
            emmaIconPartner,
          } = data;
          const contentoAvators = {
            chatBotHomeIcon: chatbotIconHome.desktop.src,
            chatBotIcon: chatbotIconOther.desktop.src,
            discliamerIcon: emmaIconPartner.desktop.src,
            errorMessageIcon: chatbotIconBubble.desktop.src,
            chatBotHeaderIcon: chatbotIconBubble.desktop.src,
          };
          yield put({
            type: FETCH_CONTENTO_AVATORS_SUCCESS,
            payload: contentoAvators,
          });
          return;
        } else {
          yield put({ type: FETCH_CONTENTO_AVATORS_FAILURE, payload: {} });
        }
      } catch (e) {
        yield put({ type: FETCH_CONTENTO_AVATORS_FAILURE, payload: {} });
        return;
      }
    } else {
      yield put({ type: SET_AVATORS, payload: avatorsConfig.avators });
      return;
    }
  } while (true);
}

function* homeSaga() {
  yield take(FRAME_APP_MOUNTED);
  // yield fork(fetchProfileFeatures); // replaced by userSaga
  const newsApiUrl = yield select(getHomepageNewsApiUrl);

  const { contentoNewsUrl, axaBlogApiUrl } = newsApiUrl;

  if (contentoNewsUrl) {
    yield fork(fetchHealthNews);
  }

  if (axaBlogApiUrl) {
    yield fork(fetchAxaBlog);
  }

  yield fork(fetchContentoAvators);
  yield fork(fetchTermsAndCondition);
  // HKEMMA-102970 - /banners API decomission as depreciated since Emma1.5
  // yield fork(fetchMarketplacePromotionNews);
  yield fork(pushEventToGA);
}

export default homeSaga;
